import { session } from '@/utils/session'
import { usesFirebaseSession } from '@/utils/session-config'
import endpoints from '@/utils/endpoints.js'
import axios, { cfAxios } from '@/utils/init-axios.js'
import { Role, User } from '@/utils/interfaces'
import moment from 'moment'
import Vue from 'vue'
import { parse } from '../../../node_modules/vue-currency-input/src/index'
import { ADMIN_MERCHANT_LIST_PARAMS } from '@/utils/endpoints.handwritten.js'
import { setIntegrationTypeCookie } from '@/utils/cookies'
import {
  MerchantSettingsSaveError,
  toDjangoMerchantShape,
  toMerchantRecordUpdate
} from '@/utils/merchant-record.js'
import { toErrorList } from '@/utils/apiErrors'
import { toDjangoCustomerShape } from '@/utils/customer-record.js'
import { RETRY_QUEUED_TOAST } from '@/utils/failedActionRetry'
import {
  loadStarredReports,
  toggleStarredReport
} from '@/components/Navigation/Sidebar/starred-reports'
import { queueBucketWrite, readPreferenceBucket } from '@/utils/ui-preference'
import {
  batchRunsParams,
  gatewayError,
  toDjangoBlytzCollectShape,
  toGatewayShape
} from '@/utils/ai-collector-config.js'

/**
 * The merchant asked for does not exist, or is not this user's. Start-up recovery reads the type,
 * never the message.
 */
export class MerchantNotFoundError extends Error {
  constructor(message) {
    super(message)
    this.name = 'MerchantNotFoundError'
  }
}

/** An axios error alone does not say why; the status is what identifies it. */
function httpFailureDetail(error) {
  const status = error?.response?.status

  return status ? `HTTP ${status}` : error?.message || 'network error'
}

function starredReportsPort({ commit, getters }) {
  const generation = session.generation
  const isCurrent = () =>
    !usesFirebaseSession() || generation === session.generation
  // Resolved per call, not captured: a write can still be queued when the user switches merchant, and
  // the old tenant path is rejected once the token's merchantId claim has rotated.
  const url = () => {
    if (usesFirebaseSession()) session.assertCurrent(generation)
    const merchantId = getters.preferenceMerchantId

    return merchantId ? endpoints.uiPreference(merchantId) : ''
  }

  return {
    canPersist: () => isCurrent() && Boolean(getters.preferenceMerchantId),
    read: () => readPreferenceBucket(url()),
    write: async bucket => (await cfAxios.put(url(), bucket)).data || bucket,
    exclusive: queueBucketWrite,
    current: () => getters.starredReports || [],
    setStarred: keys => {
      if (isCurrent()) commit('setStarredReports', keys)
    },
    setBucket: bucket => {
      if (isCurrent()) commit('setUiPreferences', bucket)
    },
    onLoadError: error => {
      if (!isCurrent()) return
      console.error(
        `Starred reports: load failed (${httpFailureDetail(error)}) ${url()}`
      )
    },
    onSaveError: error => {
      if (!isCurrent()) return
      console.error(
        `Starred reports: save failed (${httpFailureDetail(error)}) ${url()}`
      )
      Vue.toasted.show('Could not save your starred reports', {
        duration: 4000,
        position: 'bottom-center'
      })
    }
  }
}

/**
 * The list window narrowed to its `count`.
 *
 * Spelled out rather than spread from {@link ADMIN_MERCHANT_LIST_PARAMS}: the two share a filter
 * but not a purpose, and a caller reading this should not have to hold the list's sort in mind to
 * see what the count asks for. Omitting `sort` does not avoid an ORDER BY — the resource's
 * `defaultSort` applies either way — it just says that no order was wanted.
 */
const ADMIN_MERCHANT_COUNT_PARAMS = {
  isActive__exact: 'true',
  limit: 1,
  page: 1
}

/**
 * Whether the gateway will admit this user to the admin scope. The `super_admin` ROLE, not the `super`
 * group. Django mints the Firebase token's `user_type: 'super'` — which is what the gateway's admin
 * scope admits — from the prbac role slugs (`paybytxt/services/auth_service.py` `firebase_identity`),
 * while `rootGetters.isSuper` reads the `groups` claim, which comes from `user.groups.all()`.
 */
function isSuperTenant(rootGetters) {
  return Boolean(rootGetters.accessClaims?.roles?.includes('super_admin'))
}

/**
 * How many merchants the signed-in user may switch between.
 *
 * A merchant user is 1 without asking: Django's `User.organization` is a single FK, and the admin
 * list is super-only at the gateway, so the request would be a 403 rather than an answer.
 */
async function countMerchants(rootGetters) {
  if (!isSuperTenant(rootGetters)) {
    return 1
  }

  try {
    const res = await cfAxios.get(endpoints.adminMerchants(), {
      params: ADMIN_MERCHANT_COUNT_PARAMS
    })

    return res.data.count
  } catch (error) {
    // This number decides whether the navbar offers a merchant switcher, and nothing else. The gateway is
    // reached with the Firebase token, whose mint Django treats as non-fatal, so it can be missing — and
    // cfAxios then sends the Django JWT, which the gateway refuses.
    console.error('Merchant count unavailable; hiding the switcher.', error)

    return 1
  }
}

/**
 * The pkid core-service addresses a merchant by, for the organization UUID the app holds. A merchant
 * user has one organization: the token's merchant claim carries its pkid, and the profile's
 * `organization.pkid` is the backup when the claim is absent. For a super user, the caller's hint,
 * then the current merchant when it is the one asked for; otherwise the admin list answers it.
 */
async function resolveMerchantPkid(
  { dispatch, rootGetters },
  merchantID,
  pkid
) {
  if (!isSuperTenant(rootGetters)) {
    if (rootGetters.tokenMerchantId) {
      // The getter answers a string, and other readers want it so; every branch here is a number.
      return Number(rootGetters.tokenMerchantId)
    }

    if (!rootGetters.profile) {
      await dispatch('getProfile')
    }

    const profilePkid = rootGetters.profile?.organization?.pkid

    if (profilePkid == null) {
      throw new Error('The profile carries no organization pkid.')
    }

    return profilePkid
  }

  if (pkid != null) {
    return pkid
  }

  const current = rootGetters.currentMerchant

  if (current?.id === merchantID && current.pkid != null) {
    return current.pkid
  }

  const res = await cfAxios.get(endpoints.adminMerchants(), {
    params: { id__exact: merchantID, limit: 1 }
  })
  const row = res.data.results?.[0]

  if (row?.pkid == null) {
    throw new MerchantNotFoundError(`No merchant with id ${merchantID}.`)
  }

  return row.pkid
}

/**
 * One merchant, in the shape Django's retrieve returned. Commits nothing. Without a Firebase token the
 * gateway would refuse the request, so Django's retrieve answers instead — decided before any call,
 * never as a fallback from a failed one (c.28973). With a token, every failure of core's read rejects,
 * including core's 502 when it cannot answer the BlytzCollect flags.
 */
async function readMerchantRecord(ctx, { merchantID, pkid }) {
  if (!ctx.rootGetters.firebaseToken) {
    const res = await axios.get(
      endpoints.retrieveMerchantOrganization(merchantID)
    )

    return res.data
  }

  const merchantPkid = await resolveMerchantPkid(ctx, merchantID, pkid)
  const res = await cfAxios.get(endpoints.merchantRecord(merchantPkid))
  const merchant = toDjangoMerchantShape(res.data)

  if (merchant.id !== merchantID) {
    throw new MerchantNotFoundError(
      `Merchant ${merchantPkid} is not ${merchantID}.`
    )
  }

  return merchant
}

/**
 * Saves the General Settings form and answers the merchant in Django's retrieve shape.
 * @throws {MerchantSettingsSaveError} on any failure of core's PUT; Django's errors reject unchanged.
 */
async function saveMerchantSettings(
  ctx,
  { merchantID, pkid, settings, superUser }
) {
  // Decided before any call, never as a fallback from a failed one, as for readMerchantRecord.
  if (!ctx.rootGetters.firebaseToken) {
    const res = await axios.patch(
      endpoints.updateMerchantOrganization(merchantID),
      settings
    )

    return res.data
  }

  try {
    const merchantPkid = await resolveMerchantPkid(ctx, merchantID, pkid)
    // A merchant user's pkid is the token's, whatever the form holds; never write it onto another.
    if (pkid != null && Number(pkid) !== Number(merchantPkid)) {
      throw new MerchantSettingsSaveError(
        'These settings are for another merchant. Reload the page and try again.'
      )
    }
    const res = await cfAxios.put(
      endpoints.merchantRecord(merchantPkid),
      toMerchantRecordUpdate(settings, { superUser })
    )

    return toDjangoMerchantShape(res.data)
  } catch (error) {
    if (error instanceof MerchantSettingsSaveError) {
      throw error
    }
    if (!error?.response) {
      console.error('Merchant settings save failed before a response:', error)
    }
    throw new MerchantSettingsSaveError(
      toErrorList(error?.response?.data?.message)[0]
    )
  }
}

/** A location read or write failed with a message meant for the user. */
export class LocationRequestError extends Error {
  constructor(message) {
    super(message)
    this.name = 'LocationRequestError'
  }
}

/** core refused a location delete because something still uses it; `blockers` is core's 409 body. */
export class LocationInUseError extends LocationRequestError {
  constructor(message, blockers) {
    super(message)
    this.name = 'LocationInUseError'
    this.blockers = blockers
  }
}

async function resolveLocationPkid(ctx, merchantID, locationID, knownPkid) {
  const findPkid = locations =>
    locations?.find(location => location.id === locationID)?.pkid

  const merchantPkid = await resolveMerchantPkid(ctx, merchantID)
  let locationPkid = knownPkid ?? findPkid(ctx.rootGetters.locations)
  if (locationPkid == null) {
    const current = ctx.rootGetters.currentMerchant
    if (current?.id === merchantID) {
      await ctx.dispatch('getLocations', current)
      locationPkid = findPkid(ctx.rootGetters.locations)
    } else {
      // Another merchant's list must not land in the store, which every screen reads as the current one's.
      for (let page = 1; locationPkid == null; page++) {
        const res = await cfAxios.get(
          endpoints.merchantLocations(merchantPkid),
          { params: { page, pageSize: 500 } }
        )
        const rows = res.data?.results ?? []
        locationPkid = findPkid(rows)
        if (rows.length < 500 || page * 500 >= (res.data?.count ?? 0)) {
          break
        }
      }
    }
  }
  if (locationPkid == null) {
    throw new LocationRequestError(
      'This location could not be found for this merchant.'
    )
  }

  return { merchantPkid, locationPkid }
}

function toDjangoLocationShape(location) {
  return {
    name: location.name,
    phone_number: location.phoneNumber,
    address: location.address,
    city: location.city,
    state_province: location.stateProvince,
    postal_code: location.postalCode,
    incomm_biller_id: location.incommBillerId
  }
}

// The account screen and the promise modal read Django's PromiseListSerializer keys.
function toDjangoPromiseShape(promise) {
  return {
    pkid: promise.pkid,
    id: promise.id,
    account: promise.account,
    customer: promise.customer,
    merchant: promise.merchant,
    payment_method: promise.paymentMethod,
    amount: promise.amount,
    scheduled_date: promise.scheduledDate,
    confirmed: promise.confirmed,
    processed: promise.processed,
    status: promise.status,
    first_name: promise.firstName,
    last_name: promise.lastName,
    created: promise.created,
    updated: promise.updated,
    user_first_name: promise.userFirstName,
    user_last_name: promise.userLastName,
    cash: promise.cash,
    payment_method_last_four: promise.paymentMethodLastFour,
    created_by: promise.createdBy,
    external_id: promise.externalId
  }
}

// core refuses any key outside its location fields, so the form's report-email fields stay out.
function toCoreLocationFields(form) {
  return {
    name: form.name,
    address: form.address,
    city: form.city,
    stateProvince: form.state_province,
    postalCode: form.postal_code,
    phoneNumber: form.phone_number,
    incommBillerId: form.incomm_biller_id?.trim() || null
  }
}

function toLocationRequestError(error, action) {
  if (error instanceof LocationRequestError) {
    return error
  }
  if (!error?.response) {
    console.error(`${action} failed before a response:`, error)
  }

  return new LocationRequestError(
    toErrorList(error?.response?.data?.message)[0]
  )
}

/**
 * Reads one location and its Deposit and ACH Return recipients for the location detail screen.
 * @param {object} ctx - the action context
 * @param {object} payload - the route's ids
 * @param {string} payload.merchantID - the merchant UUID
 * @param {string} payload.locationID - the location UUID
 * @returns {Promise<{location: object, reports: {DEPOSIT: ?string, ACHR: ?string}, locationPkid: number}>}
 * @throws {LocationRequestError} when the route's location is not the merchant's
 */
async function readLocationDetail(ctx, { merchantID, locationID }) {
  const { merchantPkid, locationPkid } = await resolveLocationPkid(
    ctx,
    merchantID,
    locationID
  )
  const [locationRes, reportsRes] = await Promise.all([
    cfAxios.get(endpoints.merchantLocation(merchantPkid, locationPkid)),
    cfAxios.get(endpoints.locationEmailReports(merchantPkid, locationPkid))
  ])

  return {
    location: toDjangoLocationShape(locationRes.data),
    reports: { DEPOSIT: reportsRes.data.DEPOSIT, ACHR: reportsRes.data.ACHR },
    locationPkid
  }
}

/**
 * Saves a location's Deposit and ACH Return recipients with one core PUT.
 * @param {object} ctx - the action context
 * @param {object} payload - the save
 * @param {string} payload.merchantID - the merchant UUID
 * @param {string} payload.locationID - the location UUID
 * @param {?number} payload.locationPkid - skips the store lookup when known
 * @param {object} payload.reports - `{ DEPOSIT, ACHR }` recipient strings
 * @returns {Promise<object>} the saved `{ DEPOSIT, ACHR }`
 * @throws {LocationRequestError} on any failure
 */
async function saveLocationEmailReports(
  ctx,
  { merchantID, locationID, locationPkid, reports }
) {
  try {
    const resolved = await resolveLocationPkid(
      ctx,
      merchantID,
      locationID,
      locationPkid
    )
    const res = await cfAxios.put(
      endpoints.locationEmailReports(
        resolved.merchantPkid,
        resolved.locationPkid
      ),
      { DEPOSIT: reports.DEPOSIT, ACHR: reports.ACHR }
    )

    return res.data
  } catch (error) {
    throw toLocationRequestError(error, 'Location email reports save')
  }
}

/**
 * Creates a location on core from the location form's fields.
 * @param {object} ctx - the action context
 * @param {object} payload - the create
 * @param {string} payload.merchantID - the merchant UUID
 * @param {object} payload.form - the form's snake_case fields; its report emails are not sent
 * @returns {Promise<object>} core's row, camelCase, with `twilioSetup` `"ok"` or `"failed"`
 * @throws {LocationRequestError} on any failure; nothing was created
 */
async function createLocation(ctx, { merchantID, form }) {
  try {
    const merchantPkid = await resolveMerchantPkid(ctx, merchantID)
    const res = await cfAxios.post(
      endpoints.merchantLocations(merchantPkid),
      toCoreLocationFields(form)
    )

    return res.data
  } catch (error) {
    throw toLocationRequestError(error, 'Location create')
  }
}

/**
 * Saves the location fields that differ from `saved` with core's PUT, which changes only the keys it
 * is sent: core checks each key it gets, so an untouched legacy value cannot block an unrelated edit.
 * @param {object} ctx - the action context
 * @param {object} payload - the save
 * @param {string} payload.merchantID - the merchant UUID
 * @param {string} payload.locationID - the location UUID
 * @param {?number} payload.locationPkid - skips the store lookup when known
 * @param {object} payload.form - the form's snake_case fields; its report emails are not sent
 * @param {object} payload.saved - the same fields as last read or saved
 * @returns {Promise<?object>} core's full row, camelCase; null when no field changed and nothing was sent
 * @throws {LocationRequestError} on any failure
 */
async function updateLocation(
  ctx,
  { merchantID, locationID, locationPkid, form, saved }
) {
  const before = toCoreLocationFields(saved)
  const changed = Object.fromEntries(
    Object.entries(toCoreLocationFields(form)).filter(
      ([key, value]) => value !== before[key]
    )
  )
  if (!Object.keys(changed).length) {
    return null
  }

  try {
    const resolved = await resolveLocationPkid(
      ctx,
      merchantID,
      locationID,
      locationPkid
    )
    const res = await cfAxios.put(
      endpoints.merchantLocation(resolved.merchantPkid, resolved.locationPkid),
      changed
    )

    return res.data
  } catch (error) {
    throw toLocationRequestError(error, 'Location save')
  }
}

/**
 * Deletes a location on core. core refuses while anything uses it, and deletes nothing then.
 * @param {object} ctx - the action context
 * @param {object} payload - the delete
 * @param {string} payload.merchantID - the merchant UUID
 * @param {string} payload.locationID - the location UUID
 * @param {?number} payload.locationPkid - skips the store lookup when known
 * @returns {Promise<void>}
 * @throws {LocationInUseError} when core answers 409 `LOCATION_IN_USE`
 * @throws {LocationRequestError} on any other failure
 */
async function deleteLocation(ctx, { merchantID, locationID, locationPkid }) {
  let resolved
  try {
    resolved = await resolveLocationPkid(
      ctx,
      merchantID,
      locationID,
      locationPkid
    )
  } catch (error) {
    throw toLocationRequestError(error, 'Location lookup')
  }

  try {
    await cfAxios.delete(
      endpoints.merchantLocation(resolved.merchantPkid, resolved.locationPkid)
    )
  } catch (error) {
    const body = error?.response?.data
    if (body?.errorCode === 'LOCATION_IN_USE') {
      throw new LocationInUseError(body.message, body.blockers)
    }
    // core's 502 here means its payment-service count failed, and it deleted nothing.
    if (error?.response?.status === 502) {
      throw new LocationRequestError(
        'The check for what still uses this location could not run, so nothing was deleted. Try again.'
      )
    }
    throw toLocationRequestError(error, 'Location delete')
  }
}

/**
 * Reads one customer record in Django's retrieve shape: from core with a Firebase token, else Django.
 * `checkIntegration` reaches the server as given, and only the string `false` skips the DMS sync.
 * @param {object} ctx - the action context
 * @param {object} payload - which customer
 * @param {string} payload.merchantID - the merchant UUID
 * @param {?number} payload.merchantPkid - the merchant pkid, when the caller holds it
 * @param {string} payload.customerID - the customer UUID
 * @param {boolean|string} [payload.checkIntegration] - left out, the server syncs
 * @returns {Promise<object>} the customer, snake_case
 * @throws {Error} the request's own error, so a failed DMS sync still rejects with its 503
 */
async function readCustomerRecord(
  ctx,
  { merchantID, merchantPkid, customerID, checkIntegration }
) {
  const params =
    checkIntegration === undefined ? {} : { checkIntegration: checkIntegration }

  if (!ctx.rootGetters.firebaseToken) {
    const response = await axios.get(
      endpoints.retrieveCustomer(merchantID, customerID),
      { params }
    )

    return response.data
  }

  const pkid = await resolveMerchantPkid(ctx, merchantID, merchantPkid)
  const response = await cfAxios.get(
    endpoints.merchantCustomer(pkid, customerID),
    { params }
  )

  return toDjangoCustomerShape(response.data)
}

/**
 * Django's customer list carries accounts without a pkid, and core's payment history is keyed on
 * one, so a modal fed by the list resolves it from the customer's full record.
 * @throws {Error} when the customer has no account with that id.
 */
async function lookUpAccountPkid(ctx, ids) {
  const { customerID, accountID } = ids
  const customer = await readCustomerRecord(ctx, {
    ...ids,
    checkIntegration: false
  })
  const pkid = customer?.accounts?.find(
    account => account.id === accountID
  )?.pkid
  if (!pkid) {
    throw new Error(`Account ${accountID} not found on customer ${customerID}`)
  }

  return pkid
}

/**
 * The current merchant's pkid, but only when it is the merchant the caller asked about: a reply for a
 * merchant the user has since switched away from must not land under the new one.
 * @param {object} getters The datastore getters.
 * @param {string} merchantID The merchant UUID the caller named.
 * @returns {number|null} The pkid, or null when there is none or the merchants differ.
 */
function currentMerchantPkid(getters, merchantID) {
  const current = getters.currentMerchant

  return merchantID && current?.id === merchantID && current?.pkid != null
    ? current.pkid
    : null
}

/**
 * Whether a list action may clear its state: only for the merchant on screen, or when there is none.
 * A late call for a merchant the user has left must not wipe the new merchant's rows.
 * @param {object} getters The datastore getters.
 * @param {string} merchantID The merchant UUID the caller named.
 * @returns {boolean} True when clearing is safe.
 */
function asksCurrentMerchant(getters, merchantID) {
  const current = getters.currentMerchant

  return !current || !merchantID || current.id === merchantID
}

export default {
  /**
   * Reads a merchant without making it the current one — for a screen that shows a merchant the
   * user picked. `getMerchant` is the action that switches.
   */
  readMerchantRecord,
  saveMerchantSettings,
  readLocationDetail,
  saveLocationEmailReports,
  createLocation,
  updateLocation,
  deleteLocation,
  async getMerchant({ commit, dispatch, rootGetters }, { merchantID, pkid }) {
    const generation = session.generation
    const appliedMerchantID =
      merchantID || localStorage.getItem('current_merchant_id')

    if (appliedMerchantID) {
      const [merchant, numMerchants] = await Promise.all([
        readMerchantRecord(
          { dispatch, rootGetters },
          { merchantID: appliedMerchantID, pkid }
        ),
        countMerchants(rootGetters)
      ])

      if (usesFirebaseSession()) session.assertCurrent(generation)
      setIntegrationTypeCookie(merchant.integration_type)
      commit('setCurrentMerchant', { merchant })
      commit('setNumMerchants', { numMerchants })
      localStorage.setItem('current_merchant_id', merchant.id)

      await Promise.allSettled([
        dispatch('getLocations', merchant),
        dispatch('getAccountTags')
      ])

      return merchant
    }

    // No merchant to load yet. Resolve WHICH merchant, then re-enter by id rather than commit anything
    // here: the branch above is the only place that commits, so the app sees one shape (the mapped record,
    // snake_case) and the dependants are dispatched once.
    if (isSuperTenant(rootGetters)) {
      // The same list the switcher reads, narrowed to its first row: only that row's `id` and `pkid` are
      // used here, and `count` still answers the empty case. Asking for the full page would pull up to a
      // thousand rows over the wire on every cold login to read one row.
      let res

      try {
        res = await cfAxios.get(endpoints.adminMerchants(), {
          params: { ...ADMIN_MERCHANT_LIST_PARAMS, limit: 1 }
        })
      } catch (error) {
        console.error(
          'Could not resolve a default merchant from the admin list.',
          error
        )

        return null
      }

      if (res.data.count === 0) {
        return res
      }

      const [first] = res.data.results

      if (usesFirebaseSession()) session.assertCurrent(generation)

      return dispatch('getMerchant', { merchantID: first.id, pkid: first.pkid })
    }

    // The profile carries this user's one organization. `main.ts` fires `getProfile` and this
    // action inside the same `Promise.all`, so it may not have landed — awaited here rather than
    // assumed, and skipped when the store already holds it.
    if (!rootGetters.profile) {
      await dispatch('getProfile')
    }

    if (usesFirebaseSession()) session.assertCurrent(generation)
    const organizationId = rootGetters.profile?.organization?.id

    if (!organizationId) {
      return null
    }

    return dispatch('getMerchant', { merchantID: organizationId })
  },
  async fetchAiBatchConfigs(
    { commit, getters },
    { merchantID, page = 1, pageSize = 100 }
  ) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    if (!merchantPkid) {
      if (asksCurrentMerchant(getters, merchantID)) {
        commit('setAiBatchConfigs', [])
        commit('setAiBatchConfigsCount', 0)
      }

      return []
    }
    const stillCurrent = () =>
      currentMerchantPkid(getters, merchantID) === merchantPkid

    commit('setAiBatchConfigsLoading', true)
    try {
      const response = await cfAxios.get(
        endpoints.aiBatchConfigs(merchantPkid),
        { params: { page, pageSize } }
      )
      const data = toDjangoBlytzCollectShape(response.data || {})
      const results = data.results ?? []
      if (stillCurrent()) {
        commit('setAiBatchConfigs', results)
        commit('setAiBatchConfigsCount', data.count ?? results.length)
      }

      return results
    } catch (error) {
      console.error('Error loading AI caller batch configs:', error)
      if (stillCurrent()) {
        commit('setAiBatchConfigs', [])
        commit('setAiBatchConfigsCount', 0)
      }
      throw error
    } finally {
      commit('setAiBatchConfigsLoading', false)
    }
  },
  async fetchAiBatchConfig({ getters }, { configPkid }) {
    const merchantPkid = getters.currentMerchant?.pkid
    if (!merchantPkid) {
      throw new Error('Missing merchant pkid')
    }
    const response = await cfAxios.get(
      endpoints.aiBatchConfig(merchantPkid, configPkid)
    )

    return toDjangoBlytzCollectShape(response.data)
  },
  async fetchAiBatchConfigPreview(
    { getters },
    { configPkid, page = 1, pageSize = 10 }
  ) {
    const merchantPkid = getters.currentMerchant?.pkid
    if (!merchantPkid) {
      throw new Error('Missing merchant pkid')
    }
    try {
      const response = await cfAxios.get(
        endpoints.aiBatchConfigPreview(merchantPkid, configPkid),
        { params: { page, pageSize } }
      )

      return toDjangoBlytzCollectShape(response.data)
    } catch (error) {
      throw gatewayError(error)
    }
  },
  async fetchAiDefaultTransferNumber({ getters }) {
    const merchantPkid = getters.currentMerchant?.pkid
    if (!merchantPkid) {
      return null
    }
    const response = await cfAxios.get(
      endpoints.aiDefaultTransferNumber(merchantPkid)
    )

    return response.data?.defaultTransferNumber ?? null
  },
  async fetchAiLocationConfigs({ getters }) {
    const merchantPkid = getters.currentMerchant?.pkid
    if (!merchantPkid) {
      return []
    }
    const response = await cfAxios.get(
      endpoints.aiLocationConfigs(merchantPkid)
    )

    return Array.isArray(response.data)
      ? toDjangoBlytzCollectShape(response.data)
      : []
  },
  async fetchAiCallerAgents({ commit, getters }, { merchantID }) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    if (!merchantPkid) {
      if (asksCurrentMerchant(getters, merchantID)) {
        commit('setAiCallerAgents', [])
      }

      return []
    }
    const stillCurrent = () =>
      currentMerchantPkid(getters, merchantID) === merchantPkid

    commit('setAiCallerAgentsLoading', true)
    try {
      const response = await cfAxios.get(endpoints.aiAgents(merchantPkid), {
        params: { pageSize: 100 }
      })
      const results =
        toDjangoBlytzCollectShape(response.data || {}).results ?? []
      if (stillCurrent()) commit('setAiCallerAgents', results)

      return results
    } catch (error) {
      console.error('Error loading AI caller agents:', error)
      if (stillCurrent()) commit('setAiCallerAgents', [])
      throw error
    } finally {
      commit('setAiCallerAgentsLoading', false)
    }
  },
  async fetchAiBatchRuns(
    { commit, getters },
    { merchantID, page = 1, pageSize = 25, filters = {} }
  ) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    if (!merchantPkid) {
      if (asksCurrentMerchant(getters, merchantID)) {
        commit('setAiCallerBatches', [])
        commit('setAiCallerBatchesCount', 0)
      }

      return []
    }
    const stillCurrent = () =>
      currentMerchantPkid(getters, merchantID) === merchantPkid

    commit('setAiCallerBatchesLoading', true)
    try {
      const response = await cfAxios.get(endpoints.aiBatches(merchantPkid), {
        params: batchRunsParams({ page, pageSize, filters })
      })
      const data = toDjangoBlytzCollectShape(response.data || {})
      const results = data.results ?? []
      if (stillCurrent()) {
        commit('setAiCallerBatches', results)
        commit('setAiCallerBatchesCount', data.count ?? results.length)
      }

      return data
    } catch (error) {
      console.error('Error loading AI caller batch history:', error)
      if (stillCurrent()) {
        commit('setAiCallerBatches', [])
        commit('setAiCallerBatchesCount', 0)
      }
      throw error
    } finally {
      commit('setAiCallerBatchesLoading', false)
    }
  },
  async fetchAiBatchRunsProgress(
    { commit, getters, state },
    { merchantID, batchIds = [] }
  ) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    // The runs view polls by UUID; the route takes pkids, which the loaded rows carry.
    const ids = new Set(Array.isArray(batchIds) ? batchIds : [])
    const batchPkids = (state.aiCallerBatches || [])
      .filter(batch => ids.has(batch?.id) && batch?.pkid != null)
      .map(batch => batch.pkid)

    if (!merchantPkid || batchPkids.length === 0) {
      return []
    }

    try {
      const response = await cfAxios.get(
        endpoints.aiBatchProgress(merchantPkid),
        { params: { batchPkids: batchPkids.join(',') } }
      )
      const progressData = toDjangoBlytzCollectShape(response.data ?? [])
      if (currentMerchantPkid(getters, merchantID) === merchantPkid) {
        commit('mergeAiCallerBatchProgress', progressData)
      }

      return progressData
    } catch (error) {
      console.error('Error loading AI caller batch progress:', error)
      throw error
    }
  },
  async createAiBatchConfig({ dispatch, getters }, { merchantID, payload }) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    if (!merchantPkid) {
      throw new Error('Missing merchant ID')
    }
    try {
      await cfAxios.post(
        endpoints.aiBatchConfigs(merchantPkid),
        toGatewayShape(payload)
      )
    } catch (error) {
      throw gatewayError(error)
    }
    await dispatch('fetchAiBatchConfigs', { merchantID })
  },
  async updateAiBatchConfig(
    { dispatch, getters },
    { merchantID, pkid, payload }
  ) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    if (!merchantPkid || pkid == null) {
      throw new Error('Missing merchant ID or config ID')
    }
    // An edit writes a new version with a new pkid; the list reloads to show it.
    try {
      await cfAxios.put(
        endpoints.aiBatchConfig(merchantPkid, pkid),
        toGatewayShape(payload)
      )
    } catch (error) {
      throw gatewayError(error)
    }
    await dispatch('fetchAiBatchConfigs', { merchantID })
  },
  async deleteAiBatchConfig({ dispatch, getters }, { merchantID, pkid }) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    if (!merchantPkid || pkid == null) {
      throw new Error('Missing merchant ID or config ID')
    }
    try {
      await cfAxios.delete(endpoints.aiBatchConfig(merchantPkid, pkid))
    } catch (error) {
      throw gatewayError(error)
    }
    await dispatch('fetchAiBatchConfigs', { merchantID })
  },
  async saveAiLocationConfig({ getters }, { merchantID, pkid, payload }) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    if (!merchantPkid) {
      throw new Error('Missing merchant ID')
    }
    // The form's merchant_location is the location UUID the route names merchantLocationId.
    const { merchant_location: merchantLocationId, ...rest } = payload
    const body = { merchantLocationId, ...toGatewayShape(rest) }
    try {
      const response =
        pkid == null
          ? await cfAxios.post(
              endpoints.aiAdminLocationConfigs(merchantPkid),
              body
            )
          : await cfAxios.put(
              endpoints.aiAdminLocationConfig(merchantPkid, pkid),
              body
            )

      return toDjangoBlytzCollectShape(response.data)
    } catch (error) {
      throw gatewayError(error)
    }
  },
  async runAiBatchNow({ getters }, { merchantID, pkid }) {
    const merchantPkid = currentMerchantPkid(getters, merchantID)
    if (!merchantPkid || pkid == null) {
      throw new Error('Missing merchant ID or config ID')
    }
    try {
      const response = await cfAxios.post(
        endpoints.aiRunBatchNow(merchantPkid, pkid)
      )

      return response.data
    } catch (error) {
      throw gatewayError(error)
    }
  },
  async getLocations({ commit }, merchant) {
    // pkid, not the UUID: core keys the tenant root on an integer FK, so a UUID there is a type
    // error rather than an empty result.
    const merchantPkid = merchant?.pkid
    if (!merchantPkid) {
      // A merchant was supplied and we cannot query it: this is a switch to a merchant whose pkid has not
      // arrived. Keeping the previous merchant's locations would show one tenant's data under another's name
      // in every location-scoped screen, so clear instead.
      if (merchant) {
        commit('setLocations', [])
        commit('setCurrentLocation', undefined)
      }

      return
    }

    const response = await cfAxios.get(
      endpoints.merchantLocations(merchantPkid),
      {
        // 500 is core's maximum, five times the 100 Django silently capped these at.
        params: { page: 1, pageSize: 500 }
      }
    )

    // Undefined here would read as "nothing loaded yet" everywhere downstream.
    const locations = response.data?.results ?? []

    commit('setLocations', locations)
    // Still "lowest pkid": core's default order is pkid ascending, so no client-side sort.
    commit('setCurrentLocation', locations[0])
  },
  async getAccountTags({ commit, getters }) {
    const merchantPkid = getters.currentMerchant?.pkid
    if (!merchantPkid) {
      // Clear rather than keep the previous merchant's tags, which would show one tenant's data
      // under another's name.
      commit('setAccountTags', [])

      return
    }

    const response = await cfAxios.get(endpoints.accountTags(merchantPkid))
    commit('setAccountTags', response.data?.tags ?? [])
  },
  async getUsers({ commit }, { merchant, querystring }) {
    const response = await axios.get(endpoints.listUsers(merchant.id), {
      params: querystring
    })
    const users = response.data.results.map(user => new User(user))
    commit('setUsers', users)
  },
  async getMerchantPage({ commit }, { params }) {
    commit('setMerchantLoading', true)
    if ('search' in params) {
      params['search'] = params['search'].trim()
    }
    try {
      const res = await axios.get(
        endpoints.listMerchantOrganizationsSimplified(),
        { params: params }
      )
      const numMerchants = res.data.count
      const merchants = res.data.results
      commit('setNumMerchants', { numMerchants })
      commit('setMerchantPage', { merchants })

      return res
    } finally {
      commit('setMerchantLoading', false)
    }
  },
  setPageTitle({ commit }, pageTitle) {
    commit('setPageTitle', [...pageTitle])
  },
  async getCustomer(ctx, { customerID, checkIntegration = false }) {
    const { state, commit } = ctx
    const { id: merchantID, pkid: merchantPkid } = state.currentMerchant
    try {
      commit('setCustomerLoading', true)
      const customer = await readCustomerRecord(ctx, {
        merchantID,
        merchantPkid,
        customerID,
        checkIntegration
      })
      commit('setCurrentCustomer', customer)

      if (merchantPkid && customer?.pkid) {
        cfAxios
          .post(endpoints.customerViewed(merchantPkid, customer.pkid))
          .catch(error =>
            console.warn('Recording the customer view failed:', error)
          )
      }

      return customer
    } finally {
      commit('setCustomerLoading', false)
    }
  },
  async getFailedActions({ commit }, { merchantID, page_size = 100 }) {
    const response = await axios.get(endpoints.listFailedActions(merchantID), {
      params: { page_size }
    })
    const failedActions = response.data.results
    commit('setFailedActions', failedActions)

    return failedActions
  },
  async markActionResolved(
    { dispatch },
    { merchantID, actionID, refresh = false }
  ) {
    const response = await axios.post(
      endpoints.resolveFailedAction(merchantID, actionID)
    )
    Vue.toasted.show('Marked Action as Resolved', {
      duration: 2000,
      position: 'bottom-center'
    })
    if (refresh) {
      dispatch('getFailedActions', { merchantID })
    }

    return response
  },
  async retryAction({ dispatch }, { merchantID, actionID, refresh = false }) {
    const response = await axios.post(
      endpoints.retryFailedAction(merchantID, actionID)
    )
    // The repost is published to a broker, so there is no post result to report here.
    Vue.toasted.show(RETRY_QUEUED_TOAST, {
      duration: 6000,
      position: 'bottom-center'
    })
    if (refresh) {
      dispatch('getFailedActions', { merchantID })
    }

    return response
  },
  async openReceiptModal({ commit }, payment) {
    try {
      commit('setCurrentPayment', payment)
      // If no errors fetching open modal
      commit('setReceiptModalVisibility', true)
    } catch {
      // pass
    }
  },
  async refundPayment({ state }, { payment, total, sources }) {
    const merchantID = state.currentMerchant.id
    const response = await axios.post(endpoints.refundPayment(merchantID), {
      payment: payment,
      total: total,
      sources: sources
    })

    return response
  },
  async cancelPayment({ state }, { paymentID }) {
    const merchantID = state.currentMerchant.id
    const response = await axios.post(endpoints.cancelPayment(merchantID), {
      payment: paymentID
    })

    return response
  },
  async voidLoggedPayment({ state }, { paymentID }) {
    const merchantID = state.currentMerchant.id
    // No request body: void_logged is all-or-nothing on the payment in the URL. Errors are left to
    // propagate so the caller sees the backend's readable refusal message (not today / not most recent /
    // invoice touched) rather than a generic failure.
    const response = await axios.post(
      endpoints.voidLoggedPayment(merchantID, paymentID)
    )

    return response
  },
  async refundTransaction({ state }, { transactionID }) {
    const merchantID = state.currentMerchant.id
    const response = await axios.post(endpoints.refundTransaction(merchantID), {
      transaction: transactionID
    })

    return response
  },
  async fetchAccountPromises({ commit, state }) {
    const merchantPkid = state?.currentMerchant?.pkid
    const accountPkid = state?.currentAccount?.pkid
    if (!merchantPkid || !accountPkid) {
      // Clear rather than keep the previous account's promises on this one.
      commit('setAccountPromises', [])

      return
    }
    // An answer for an account the user has since left must not land on the new one.
    const stillCurrent = () => state.currentAccount?.pkid === accountPkid
    commit('setAccountPromisesLoading', true)
    // Until this account answers, the previous one's rows must not sit under it: Delete acts on them.
    commit('setAccountPromises', [])
    try {
      const response = await cfAxios.get(
        endpoints.accountPromises(merchantPkid, accountPkid)
      )
      const promises = response.data.promises
        .map(toDjangoPromiseShape)
        .filter(promise => moment(promise.scheduled_date).diff(moment()) > 1)
      if (stillCurrent()) commit('setAccountPromises', promises)
    } finally {
      commit('setAccountPromisesLoading', false)
    }
  },
  async fetchRole({ commit }, { merchantID, roleID }) {
    commit('setRoleLoading', true)
    try {
      const response = await axios.get(endpoints.roleDetail(merchantID, roleID))
      const role = new Role()
      role.description = response.data.role_description
      role.name = response.data.role_name
      role.settings = response.data.role_settings
      role.settings.max_merchant_payment_amount = parse(
        role.settings.max_merchant_payment_amount,
        {}
      )
      role.settings.min_merchant_payment_amount = parse(
        role.settings.min_merchant_payment_amount,
        {}
      )
      for (const permission in role.permissions) {
        const each = response.data.has_access.find(has => {
          return permission === has.name
        })
        if (each) role.permissions[permission] = true
      }
      commit('setRole', role)
    } finally {
      commit('setRoleLoading', false)
    }
  },

  async getMostRecentPayment(ctx, ids) {
    const { merchantPkid, accountPkid } = ids
    const { commit } = ctx
    commit('setRecentPaymentLoading', true)
    try {
      const pkid = accountPkid ?? (await lookUpAccountPkid(ctx, ids))
      const response = await cfAxios.get(
        endpoints.accountPaymentHistory(merchantPkid, pkid),
        { params: { page: 1, pageSize: 50 } }
      )
      const defaultDateFormat = 'YYYY-MM-DD'
      const currentDate = moment().format(defaultDateFormat)
      const data = response.data?.results
        .filter(item => {
          return (
            ['PAID', 'REFUND'].includes(item.status) &&
            // Core sends `payment: null` for an item without one, and moment(undefined) is now.
            !!item.payment?.created &&
            moment(item.payment.created).format(defaultDateFormat) ===
              currentDate
          )
        })
        .sort(
          (before, after) =>
            new Date(moment(before.payment.created)) -
            new Date(moment(after.payment.created))
        )
        .map(item => {
          return {
            created: item.payment.created,
            id: item.id,
            total_amount: item.payment.total_amount
          }
        })
      if (!data.length) return {}

      return data[0]
    } finally {
      commit('setRecentPaymentLoading', false)
    }
  },
  async voidCustomer({ state, commit }, customerID) {
    const merchantID = state.currentMerchant.id
    try {
      commit('setCustomerLoading', true)
      const response = await axios.delete(
        endpoints.voidCustomer(merchantID, customerID)
      )

      return response
    } finally {
      commit('setCustomerLoading', false)
    }
  },
  getDashboardDateRange({ commit }, dateRange) {
    commit('setDashboardDateRange', dateRange)
  },
  async getAvailableUpc() {
    const response = await axios.get(endpoints.listAvailableUpc(), {})

    return response
  },
  async getPropayBusinessTypes() {
    const response = await axios.get(endpoints.listPropayBusinessTypes(), {})

    return response
  },
  async updateCustomerBpInformation({ state, commit }, { customerUserData }) {
    const merchantId = state.currentMerchant.id
    const customerId = state.currentCustomer.id
    try {
      commit('setCustomerLoading', true)
      const response = await axios.put(
        endpoints.customerBpInformation(merchantId, customerId),
        customerUserData
      )

      return response
    } finally {
      commit('setCustomerLoading', false)
    }
  },
  async emailIsRegistered(_context, userData) {
    const outcome = { ok: false }
    try {
      await axios.post(endpoints.validateUserEmail(), userData)
    } catch (err) {
      const data = err.response?.data || []
      var defaultError = 'Email could not be validated'
      if (data.length > 0) defaultError = data[0]
      Object.assign(outcome, {
        ok: true,
        error: defaultError
      })
    }

    return outcome
  },
  // starred reports live under `starred_reports` in the same per-user
  // ui-preference bucket the customer-list column preset uses.
  async fetchStarredReports(context) {
    await loadStarredReports(starredReportsPort(context))
  },
  async toggleStarredReport(context, reportKey) {
    await toggleStarredReport(starredReportsPort(context), reportKey)
  }
}
